import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sb } from '../supabase';
import Footer from '../components/Footer';
import { runWithOptionalColumns } from '../lib/supabaseColumnFallback';
import useRequestAttachments from '../hooks/useRequestAttachments';
import Notice from '../components/Notice';
import { requestReceivedCopy, requestReceivedText } from '../lib/requestReceivedCopy';
import PhoneField, { parseStoredPhone } from '../components/PhoneField';
import { saveProfilePhoneIfBlank } from '../lib/profilePhone';
import RequestFeeNotice from '../components/RequestFeeNotice';
import { fetchFeeConfig, payFeeIfRequired } from '../lib/requestFee';
import { isPlainQuantity, normalizeQuantityInput, quantityMessage } from '../lib/requestQuantity';
import {
  ATTACH_ACCEPT, REQUEST_UNITS, DELIVERY_PREFERENCES,
  SAMPLE_REQUIREMENTS, labelFor, deliveryHintFor,
} from '../lib/requestFormOptions';
import CityPicker from '../components/CityPicker';
import { buildManagedBriefRow, generateManagedBriefWithAI } from '../lib/managedSourcing';
import { UI_CATEGORIES } from '../lib/supplierDashboardConstants';
import {
  DEFAULT_DISPLAY_CURRENCY,
  normalizeDisplayCurrency,
} from '../lib/displayCurrency';
import { requestContactNote } from '../lib/requestContactNote';
import useServiceTracking, { anyFilled } from '../hooks/useServiceTracking';

// Category options come from lib/supplierDashboardConstants UI_CATEGORIES
// (trilingual, 25 — 'all' filtered out below for the form selector).
// The count read 23 until 2026-09-21; it was measured at 25, identical in ar,
// en and zh, and is now held there by src/lib/requestFormOptions.test.js.

/* ONE pill style, used by BOTH single-choice pill groups on this form: payment
   plan and sample requirement. It was written once and copied once, which is
   the state that lets two controls that are meant to look identical drift
   apart a month later — the same "one predicate, one place" reason golden rule
   12 gives for not restating a status list per call site.
   Presentation only: it returns a style object and knows nothing about which
   field it is dressing. `on` is whether this pill is the chosen one. */
const pillStyle = (on, isAr) => ({
  padding: '7px 16px', fontSize: 12, borderRadius: 20, cursor: 'pointer', transition: 'all 0.15s',
  background: on ? 'var(--bg-raised)' : 'transparent',
  color: on ? 'var(--text-primary)' : 'var(--text-disabled)',
  border: '1px solid',
  borderColor: on ? 'var(--border-muted)' : 'var(--border-subtle)',
  fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
  minHeight: 32,
});


/* ─── Main ───────────────────────────────── */
export default function Requests({ lang, user, profile, setProfile, displayCurrency, exchangeRates }) {
  const viewerCurrency = normalizeDisplayCurrency(displayCurrency || DEFAULT_DISPLAY_CURRENCY);
  const nav = useNavigate();
  const location = useLocation();
  // unit / ship_city / delivery_preference added 2026-09-21 (migration
  // 20260921122508). All three are REQUIRED by this form and NULLABLE in the
  // column — 16 rows predate them and a CHECK that rejected those rows would
  // have made every one of them unreadable.
  const [newReq, setNewReq]       = useState({ title_ar: '', title_en: '', quantity: '', unit: '', description: '', category: 'other', budget_per_unit: '', budget_currency: viewerCurrency, payment_plan: '', sample_requirement: '', ship_city: '', delivery_preference: '', sourcing_mode: 'direct', response_deadline: '' });
  // Growth OS (step 7): «اطلب عرض سعر» (rfq) or the managed toggle on the same form.
  const svc = useServiceTracking(String(newReq.sourcing_mode || 'direct') === 'managed' ? 'managed' : 'rfq');
  useEffect(() => { if (anyFilled(newReq, ['category', 'budget_currency', 'sourcing_mode'])) svc.start(); }, [newReq, svc]);

  // Keep the form's default budget currency in sync with the viewer's
  // preferred display currency until they explicitly override it on the form.
  useEffect(() => {
    setNewReq(prev => prev.budget_per_unit ? prev : { ...prev, budget_currency: viewerCurrency });
  }, [viewerCurrency]);
  const [submitting, setSubmitting] = useState(false);
  // The request fee (299 SAR) on a managed request — stated before submit,
  // charged right after the insert (the server decides; see lib/requestFee).
  const [feeConfig, setFeeConfig] = useState(null);
  useEffect(() => { fetchFeeConfig(sb).then(setFeeConfig); }, []);
  useEffect(() => { if (String(newReq.sourcing_mode) === 'managed' && feeConfig?.enabled && feeConfig.amount) svc.feeShown(); }, [newReq.sourcing_mode, feeConfig, svc]);

  // persistKey: files survive the sign-in redirect with the draft (owner,
  // 2026-10-17) — see src/lib/draftFiles.js. reset() below clears them.
  const picker = useRequestAttachments(lang, { persistKey: 'requests-page' });
  // The outcome of the last submit, replacing two window.alert() calls in this
  // flow: { tone, text }. An alert cannot be trilingual beyond the string handed
  // to it, cannot be styled, cannot be laid out RTL, and blocks the page — and
  // there is nowhere in a modal dialog to put "published, but one file failed",
  // which is the message this phase needs to deliver. The five offer-flow alerts
  // elsewhere in this file are out of scope and untouched.
  const [submitNotice, setSubmitNotice] = useState(null);
  // Contact phone for MANAGED mode → requests.contact_phone (2026-09-24), the
  // same rule as the two other managed paths (IdeaToProduct, RequestWizard):
  // required, E.164 via PhoneField, prefilled from profiles.phone and NOT
  // written back — only the Settings page writes profiles.phone. Its own state,
  // not newReq, so the number stays out of the auto-saved draft. A direct
  // request does not ask for it: a factory quotes that one, not the concierge.
  const [phone, setPhone] = useState(() => parseStoredPhone(profile?.phone));
  const [phoneKey, setPhoneKey] = useState(0);
  const phoneTouched = useRef(false);
  useEffect(() => {
    // The profile can load after mount; PhoneField reads its value once.
    if (phoneTouched.current || !profile?.phone) return;
    setPhone(parseStoredPhone(profile.phone));
    setPhoneKey((k) => k + 1);
  }, [profile?.phone]);
  // تأكد من قيمة lang
  const effectiveLang = lang || 'en';
  const isAr       = effectiveLang === 'ar';
  const isZh       = effectiveLang === 'zh';
  const isSupplier = profile?.role === 'supplier';
  
  // Debug: تحقق من اللغة
  useEffect(() => {
    console.log('Requests page - lang:', lang, 'isSupplier:', isSupplier, 'profile role:', profile?.role, 'isAr:', isAr, 'isZh:', isZh);
  }, [lang, isSupplier, profile, isAr, isZh]);
  const isManagedMode = String(newReq.sourcing_mode || 'direct').toLowerCase() === 'managed';
  // A mode in the URL means the visitor already chose — on the home page, or
  // wherever they came from. Asking again invites them to reverse a decision
  // they just made, so the picker is hidden and the form opens straight away.
  const urlMode = String(new URLSearchParams(location.search).get('mode') || '').toLowerCase();
  const modePreset = urlMode === 'direct' || urlMode === 'managed';
  // Switching keeps whatever has been typed — it only flips the field — and
  // rewrites the URL so a reload or a shared link lands in the same mode.
  const switchMode = (next) => {
    setNewReq((prev) => ({ ...prev, sourcing_mode: next }));
    nav(`/requests?mode=${next}`, { replace: true });
  };
  const cats       = (UI_CATEGORIES[lang] || UI_CATEGORIES.ar).filter(c => c.val !== 'all');

  // Load request draft from sessionStorage on mount (buyer)
  useEffect(() => {
    if (!isSupplier) {
      const draft = sessionStorage.getItem('maabar_request_draft');
      if (draft) {
        try { setNewReq(prev => ({ ...prev, ...JSON.parse(draft) })); } catch {}
      }
    }
  }, []);

  // `direct` is honoured as explicitly as `managed` rather than left to the
  // initial state: the link states an intent, and it should not depend on what
  // the default happens to be later.
  useEffect(() => {
    if (isSupplier) return;
    if (urlMode === 'managed' || urlMode === 'direct') {
      setNewReq((prev) => ({ ...prev, sourcing_mode: urlMode }));
    }
  }, [isSupplier, urlMode]);

  // Save request form to sessionStorage on every change (buyer)
  useEffect(() => {
    if (!isSupplier) {
      sessionStorage.setItem('maabar_request_draft', JSON.stringify(newReq));
    }
  }, [newReq, isSupplier]);

  // A supplier has no business on this page any more. It used to be dual-role —
  // buyers created requests here, suppliers browsed the open book — and the
  // book is gone (2026-09-19): suppliers see only what is routed to them, on
  // their own dashboard. Send them there rather than showing them a trader's
  // create form, which is what they would otherwise get now that the
  // supplier half has been removed.
  useEffect(() => {
    if (isSupplier) nav('/dashboard', { replace: true });
  }, [isSupplier, nav]);

  // Buyers use this page as the standard request form ("ارفع طلبك",
  // sourcing_mode='direct' by default; ?mode=managed switches to the managed
  // flow). The supplier browse half — loadRequests, the open-book query, the
  // category chips, the offer forms — was REMOVED on 2026-09-19. Suppliers see
  // only what is routed to them (request_routing), on their own dashboard —
  // which since 2026-10-11 includes every open direct request, routed to all
  // operational suppliers automatically (source 'open_pool', 20261011224335).


  const submitNewRequest = async () => {
    if (!user) { nav('/login/buyer'); return; }

    const titleAr = String(newReq.title_ar || '').trim();
    const titleEn = String(newReq.title_en || '').trim();
    const fallbackTitle = titleAr || titleEn;
    // DIGITS ONLY (2026-09-21). requests.quantity is a text column and its
    // three writers disagreed: this form stored "500 units", mobile and
    // createManagedRequest stored 500. Now that `unit` is its own field, the
    // number and the unit must not both live in this string — the RFQ page
    // renders `${quantity} ${unitLabel}`, which turned "500 units" into
    // "500 units carton". The column stays text (owner, 2026-09-21): a type
    // change would touch every reader across trader, supplier, admin, the RFQ
    // RPCs, the factory invite and two email functions.
    // PLAIN NUMBER, REFUSED NOT STRIPPED (owner, 2026-09-30). The strip turned
    // «100 إلى 150» into 100150 and "1,000" into 1000 without a word; payment
    // multiplies price × quantity. Now the input keeps what was typed (only
    // Arabic-Indic / full-width digits are rewritten, normalizeQuantityInput)
    // and the guard below refuses anything isPlainQuantity rejects.
    const quantity = String(newReq.quantity || '').trim();
    const isManagedMode = String(newReq.sourcing_mode || 'direct').toLowerCase() === 'managed';
    const shipCity = String(newReq.ship_city || '').trim();
    // Hoisted from the payload block below so the guard and the row that gets
    // inserted read the SAME trimmed value. Two separate trims of the same
    // field is how a guard ends up passing on text the insert then stores as
    // empty (whitespace-only input is the case that does it).
    const description = String(newReq.description || '').trim();

    if (!fallbackTitle || !quantity || !newReq.payment_plan || !newReq.sample_requirement) {
      svc.fieldError(!fallbackTitle ? 'title' : !quantity ? 'quantity' : !newReq.payment_plan ? 'payment_plan' : 'sample_requirement');
      alert(isAr ? 'يرجى تعبئة اسم المنتج والكمية وخطة الدفع ومتطلبات العينة' : lang === 'zh' ? '请填写产品名称、数量、付款方案和样品要求' : 'Add product name, quantity, payment plan, and sample requirement');
      return;
    }

    // Through submitNotice (the inline Notice above the submit button), the
    // pattern this form is migrating to — see the description guard below.
    if (!isPlainQuantity(quantity)) {
      setSubmitNotice({ tone: 'error', text: quantityMessage(lang) });
      return;
    }

    // The destination and the delivery ask are what a factory cannot quote
    // without. Until today neither existed on this form, ship_country was
    // empty in every row, and get_public_rfq printed the literal
    // "Saudi Arabia" to every factory — which is why every quote so far is
    // EXW or FOB or carries no basis at all.
    if (!shipCity || !newReq.delivery_preference || !newReq.unit) {
      alert(isAr ? 'يرجى اختيار المدينة ووحدة الكمية وطريقة الاستلام' : lang === 'zh' ? '请选择城市、数量单位和收货方式' : 'Choose the city, the quantity unit, and how you want it delivered');
      return;
    }

    // The description became REQUIRED on 2026-09-21. Added as its own guard
    // AFTER the two existing ones rather than folded into the first, so no
    // existing path changes: the same combinations that showed the same two
    // messages before still do.
    // ⚠️ It reports through submitNotice (the inline <Notice> above the submit
    // button) while the two guards above still use alert(). That is a KNOWN
    // inconsistency, flagged to the owner, not an oversight — this form is
    // mid-migration off alert(), which the comment at the Notice render site
    // records ("where the two alert() calls used to be"). Converting the other
    // two is a separate change; doing it here would have edited validation the
    // brief said not to touch.
    if (!description) {
      setSubmitNotice({
        tone: 'error',
        text: isAr
          ? 'اكتب وصف المنتج ومواصفاته — بدونها لا يستطيع المصنع تسعير طلبك.'
          : lang === 'zh'
            ? '请填写产品描述与规格 — 否则工厂无法为您报价。'
            : 'Add the description and specifications — a factory cannot price your request without them.',
      });
      return;
    }

    if (isManagedMode && !phone.valid) {
      setSubmitNotice({
        tone: 'error',
        text: isAr
          ? 'يرجى إدخال رقم جوال صحيح — يتواصل معك مدير حسابك عليه بخصوص الطلب المُدار.'
          : lang === 'zh'
            ? '请输入有效的手机号码 — 客户经理将通过它就托管需求与您联系。'
            : 'Enter a valid mobile number — your account manager uses it to reach you about this managed request.',
      });
      return;
    }

    setSubmitting(true);
    setSubmitNotice(null);

    // Upload BEFORE the insert so the paths go in with the row. uploadAll never
    // throws: a file that does not make it is reported, the request is created
    // regardless, and the trader is told what to re-add (decision §4.1).
    const { paths: attachmentPaths, failed } = await picker.uploadAll(user.id);

    // Insert immediately with the SOURCE text — translation and the managed brief
    // are background enrichment, not validity conditions (the payload source-fills
    // every language column). Two AI round-trips no longer sit between the tap and
    // the confirmation.
    // `description` is trimmed once, up with the other guards — see there.
    const payload = {
      buyer_id: user.id,
      title_ar: titleAr || fallbackTitle,
      title_en: titleEn || fallbackTitle,
      title_zh: titleEn || titleAr || fallbackTitle,
      quantity,
      unit: newReq.unit,
      description,
      description_ar: description || null,
      description_en: description || null,
      description_zh: description || null,
      category: newReq.category || 'other',
      status: 'open',
      // The destination. ship_country is fixed to Saudi Arabia here because
      // this form offers KSA cities only — the country picker belongs to the
      // factory-quote modal, which serves the GCC. If this form ever offers
      // another country, the country must come from the picker, not from here.
      ship_city: shipCity,
      ship_country: 'Saudi Arabia',
      delivery_preference: newReq.delivery_preference,
      // request_kind was written by mobile and by createManagedRequest but
      // never by this form, so web-created direct requests were the only rows
      // in the table with a null kind.
      request_kind: isManagedMode ? 'managed' : 'direct',
      budget_per_unit: newReq.budget_per_unit ? parseFloat(newReq.budget_per_unit) : null,
      budget_currency: newReq.budget_per_unit ? 'SAR' : null,   // traders budget in SAR only
      payment_plan: newReq.payment_plan ? parseInt(newReq.payment_plan, 10) : null,
      sample_requirement: newReq.sample_requirement,
      // PRIVATE request-attachments object paths — what /f/<slug> mints signed
      // URLs for, and now the ONLY image path this page writes. The public
      // product-images dual-write (reference_image) was retired here; the column
      // is untouched on existing rows and every reader falls back to it.
      attachment_paths: attachmentPaths,
      attachments_missing: failed.length > 0,
      sourcing_mode: isManagedMode ? 'managed' : 'direct',
      managed_status: isManagedMode ? 'submitted' : null,
      managed_review_state: isManagedMode ? 'pending' : null,
      response_deadline: newReq.response_deadline || null,
      // Managed only; validated E.164 — the submit is blocked above without it.
      ...(isManagedMode ? { contact_phone: phone.e164 } : {}),
    };

    const { data: insertedRequest, error, strippedColumns } = await runWithOptionalColumns({
      table: 'requests',
      payload,
      // attachment_paths / attachments_missing ride the same fallback: a request
      // must not fail to be created because an environment is a migration
      // behind. strippedColumns still reports it below.
      // unit / ship_city / ship_country / delivery_preference / request_kind
      // ride the same fallback. They all exist in production (20260921122508
      // and 20260801000009), but a request must not fail to be created because
      // an environment is a migration behind — losing the trader's whole
      // specification over a column is the worse outcome, and strippedColumns
      // still reports it to the console below.
      optionalKeys: ['description_ar', 'description_en', 'description_zh', 'attachment_paths', 'attachments_missing', 'unit', 'ship_city', 'ship_country', 'delivery_preference', 'request_kind', 'contact_phone'],
      execute: (nextPayload) => sb.from('requests').insert(nextPayload).select('*').single(),
    });
    if (strippedColumns && strippedColumns.length > 0) {
      console.warn('[submitNewRequest] Columns stripped (not in DB schema yet — run migration):', strippedColumns);
    }

    setSubmitting(false);
    if (error) {
      setSubmitNotice({
        tone: 'error',
        text: isAr ? 'حدث خطأ أثناء رفع الطلب' : lang === 'zh' ? '提交需求时出错' : 'Something went wrong while posting the request',
      });
      return;
    }
    svc.submitted({ request_id: insertedRequest?.id, category: newReq.category || 'other', has_attachments: (attachmentPaths?.length || 0) > 0, fee_required: isManagedMode });

    // ═══ REMOVED 2026-09-21 — the client-side translation block ════════════
    // An unawaited buildTranslatedRequestFields() ran here, in the browser
    // tab, and patched the six language columns afterwards. It was the cause
    // of a real defect, not a tidy-up: if the trader closed the tab before it
    // resolved, the raw source text stayed in all six columns forever — eight
    // of fourteen requests were in that state — and a Chinese factory was
    // shown Arabic.
    //
    // The SERVER owns this now: the translate-request edge function detects
    // the script, translates to the other two languages, writes all six
    // columns and records the outcome in requests.translation_status
    // (20260921112056), with request_translation_is_clean() gating what
    // reaches a supplier. The row is inserted with the trader's own text and
    // replaced seconds later.
    //
    // ⚠️ DO NOT REINTRODUCE A CLIENT TRANSLATION CALL HERE. The source
    // language must be detected from the SCRIPT, never from which field the
    // text was typed into — deciding it by field is what produced
    // title_ar = "Construction Site Materials" on M-10079.
    // ═══════════════════════════════════════════════════════════════════════

    // First phone this trader has given us → their profile, so the next
    // request prefills. Managed only (the only mode that asks); blank profiles
    // only; never overwrites. Fire-and-forget.
    if (isManagedMode) {
      saveProfilePhoneIfBlank({ userId: user.id, profile, e164: phone.e164 })
        .then((saved) => { if (saved) setProfile?.((p) => (p ? { ...p, phone: phone.e164 } : p)); });
    }

    // Background AI brief (managed only) → advance to admin_review. maabar-ai
    // self-persists server-side (requestId); the client upsert is the pre-deploy fallback.
    if (isManagedMode && insertedRequest?.id) {
      (async () => {
        try {
          const brief = await generateManagedBriefWithAI({ request: payload, lang, requestId: insertedRequest.id, buyerId: user.id });
          await sb.from('managed_request_briefs').upsert(buildManagedBriefRow({ requestId: insertedRequest.id, buyerId: user.id, brief }), { onConflict: 'request_id' });
          await sb.from('requests').update({
            managed_status: 'admin_review',
            managed_priority: brief.priority || 'normal',
            managed_ai_ready_at: new Date().toISOString(),
          }).eq('id', insertedRequest.id);
        } catch (managedError) {
          console.error('managed request setup error:', managedError);
        }
      })();
    }

    // One confirmation for every request type (owner, 2026-10-03) — see
    // src/lib/requestReceivedCopy.js. It replaced "suppliers will contact you
    // soon" and the managed brief/top-3 line, which promised more than 3–7
    // working days.
    const successText = `${requestReceivedCopy(lang).title}\n${requestReceivedText(lang)}`;

    // One notice, two possible tones. A failed upload does NOT become an error:
    // the request was posted, and the reference the trader is about to see is
    // real. The warning names the files so they can be re-added later.
    if (failed.length > 0) {
      const names = failed.map((f) => f.name).join(isAr ? '، ' : ', ');
      setSubmitNotice({
        tone: 'warn',
        text: `${successText} ${
          isAr ? `لكن تعذّر رفع: ${names}. يمكنك إضافتها لاحقاً من صفحة الطلب.`
            : lang === 'zh' ? `但以下文件未能上传：${names}。您可以稍后在需求页面补充。`
              : `However, these did not upload: ${names}. You can add them later from the request page.`
        }`,
      });
    } else {
      setSubmitNotice({ tone: 'ok', text: successText });
    }
    // The picked files are gone with the form; the notice above is the only
    // record of what failed, so it must not be cleared with them.
    picker.reset();
    sessionStorage.removeItem('maabar_request_draft');
    setNewReq({ title_ar: '', title_en: '', quantity: '', unit: '', description: '', category: 'other', budget_per_unit: '', budget_currency: viewerCurrency, payment_plan: '', sample_requirement: '', ship_city: '', delivery_preference: '', sourcing_mode: 'direct', response_deadline: '' });
    // A managed request used to navigate away immediately. That was safe while
    // the confirmation was a blocking alert() — the trader had to dismiss it
    // before the redirect could happen. An inline Notice does not block, so
    // navigating now would flash the warning and throw it away.
    //
    // So: navigate on a clean submit, as before; STAY PUT when something failed
    // to upload, because that notice is the only place the failed filenames
    // exist. The trader reads it and moves on by their own click.
    // Managed: the request fee, straight to Telr (the failed-upload notice, if
    // any, is lost — the fee must not wait on it; the request page shows the
    // attachments). If the page cannot open, the dashboard card offers the fee.
    if (isManagedMode && insertedRequest?.id) {
      try {
        if (await payFeeIfRequired(sb, insertedRequest.id)) return;   // leaving for Telr
      } catch (feeErr) {
        console.error('[Requests] could not start the request fee payment:', feeErr?.message || feeErr);
        // Not silent: the trader must know the fee is still unpaid. Blocking on
        // purpose — the navigation below would otherwise carry them past it.
        alert(isAr
          ? 'تم حفظ طلبك، لكن تعذّر فتح صفحة الدفع. لم يُخصم أي مبلغ — تقدر تدفع رسوم تأكيد الطلب من «طلباتي».'
          : "Your request is saved, but the payment page couldn't open. Nothing was charged — you can pay the request confirmation fee from My requests.");
      }
    }
    if (isManagedMode && insertedRequest?.id && failed.length === 0) {
      nav('/dashboard?tab=requests&request=' + insertedRequest.id);
    }
  };


  return (
    <div className="supplier-req-wrap">

      {/* ── Header ─────────────────────────── */}
      <div className="page-header">
        <div>
          <h1 className={`page-title${isAr ? ' ar' : ''}`}>
            {isSupplier
              ? (isAr ? 'طلبات التجار' : lang === 'zh' ? '采购需求' : 'Trader Requests')
              : (isAr ? 'ارفع طلبك' : lang === 'zh' ? '发布需求' : 'Post Your Request')}
          </h1>
          <p className={`page-sub${isAr ? ' ar' : ''}`}>
            {isSupplier
              ? (isAr ? 'قدّم عرضاً احترافياً وواضحاً على الطلبات المفتوحة' : lang === 'zh' ? '针对开放需求提交更专业、更清晰的报价' : 'Submit a cleaner, more professional quote on open requests')
              : (isAr ? 'اكتب طلبك وموردون صينيون يتنافسون لك' : lang === 'zh' ? '发布您的需求，让供应商为您报价竞争' : 'Post your request and suppliers compete for you')}
          </p>
        </div>
      </div>

      {/* ════════════════════════════════════
          واجهة التاجر / الزائر
      ════════════════════════════════════ */}
      {!isSupplier && (
        <div style={{ padding: 'clamp(24px, 5vw, 40px) clamp(16px, 6vw, 60px)', maxWidth: 820, width: '100%', boxSizing: 'border-box', margin: '0 auto' }}>

          {/* Draft restore banner */}
          {user && sessionStorage.getItem('maabar_request_draft') && (() => {
            try { const d = JSON.parse(sessionStorage.getItem('maabar_request_draft')); return d.title_ar || d.title_en; } catch { return false; }
          })() && (
            <div style={{
              background: 'rgba(58,122,82,0.1)', border: '1px solid rgba(58,122,82,0.2)',
              padding: '12px 18px', borderRadius: 'var(--radius-lg)', marginBottom: 20,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 13, color: '#5a9a72', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {isAr ? '📝 طلبك محفوظ — أكمل الإرسال' : lang === 'zh' ? '📝 您的草稿已保存 — 完成提交' : '📝 Your draft is saved — complete submission'}
              </span>
            </div>
          )}

          {/* إرشاد للزائر */}
          {!user && (
            <div style={{
              background: 'var(--bg-subtle)',
              border: '1px solid var(--border-subtle)',
              padding: '18px 22px',
              borderRadius: 'var(--radius-lg)',
              marginBottom: 32,
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 12,
            }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {isAr ? 'كيف يشتغل مَعبر؟' : lang === 'zh' ? 'Maabar 如何运作？' : 'How does Maabar work?'}
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {isAr
                    ? 'اكتب طلبك ← موردون صينيون يرسلون عروضهم ← اختر الأفضل ← استلم بضاعتك'
                    : lang === 'zh' ? '发布需求 ← 供应商报价 ← 选择最佳报价 ← 收货'
                    : 'Post request ← Suppliers send offers ← Choose the best ← Receive your order'}
                </p>
              </div>
              <button onClick={() => nav('/login/buyer')} style={{
                background: '#1a1a1a', color: '#ffffff',
                border: 'none', padding: '9px 20px',
                fontSize: 12, fontWeight: 500,
                cursor: 'pointer', borderRadius: 'var(--radius-md)',
                whiteSpace: 'nowrap', transition: 'all 0.15s',
                minHeight: 36,
              }}>
                {isAr ? 'تسجيل مجاني' : lang === 'zh' ? '免费注册' : 'Free Sign Up'}
              </button>
            </div>
          )}

          {!modePreset && (
          <div style={{
            marginBottom: 18,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12,
          }}>
            {[
              {
                key: 'managed',
                featured: true,
                title: isAr ? 'دع معبر يتولى الطلب' : lang === 'zh' ? '让 Maabar 代您处理需求' : 'Let Maabar handle the request',
                eyebrow: isAr ? 'الأكثر شمولاً' : lang === 'zh' ? '最完整的路径' : 'Most guided path',
                text: isAr ? 'معبر يجهز الـ brief، يراجع الطلب، يختار الموردين الأنسب فقط، ثم يعرض لك أفضل 3 عروض هنا داخل نفس الطلب.' : lang === 'zh' ? 'Maabar 会整理 brief、审核需求、只匹配合适供应商，然后在同一需求页里给您最佳 3 个方案。' : 'Maabar prepares the brief, reviews the request, matches only suitable suppliers, then returns the top 3 offers in the same request page.',
                onClick: () => setNewReq(prev => ({ ...prev, sourcing_mode: 'managed' })),
              },
              {
                key: 'direct',
                title: isAr ? 'ارفع طلبك بنفسك' : lang === 'zh' ? '自己发布需求' : 'Post your request yourself',
                eyebrow: isAr ? 'للطلبات الواضحة' : lang === 'zh' ? '适合明确需求' : 'For clear requests',
                text: isAr ? 'إذا كان المنتج واضحاً عندك وتريد عروضاً مباشرة، استخدم نموذج الطلب القياسي.' : lang === 'zh' ? '如果产品已经明确并想直接收报价，请使用标准需求表单。' : 'If the product is already clear and you want direct quotes, use the standard request form.',
                onClick: () => setNewReq(prev => ({ ...prev, sourcing_mode: 'direct' })),
              },
            ].map((option) => {
              const active = option.key === 'managed' ? isManagedMode : !isManagedMode;
              return (
                <div key={option.key} style={{ background: option.featured ? 'var(--bg-subtle)' : 'var(--bg-subtle)', border: `1px solid ${active ? 'rgba(0,0,0,0.15)' : option.featured ? 'rgba(0,0,0,0.08)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-xl)', padding: '18px 20px' }}>
                  <p style={{ fontSize: 10, letterSpacing: 1.8, textTransform: 'uppercase', color: option.featured ? 'var(--text-primary)' : 'var(--text-disabled)', marginBottom: 8 }}>
                    {option.eyebrow}
                  </p>
                  <h3 style={{ fontSize: 18, fontWeight: 400, color: 'var(--text-primary)', marginBottom: 10, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{option.title}</h3>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 14, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {option.text}
                  </p>
                  <button type="button" className={active ? 'btn-primary' : 'btn-outline'} onClick={option.onClick} style={{ minHeight: 36, fontSize: 12 }}>
                    {option.key === 'managed'
                      ? (isAr ? 'اختيار الطلب المُدار' : lang === 'zh' ? '选择托管需求' : 'Choose managed sourcing')
                      : (isAr ? 'اختيار الطلب القياسي' : lang === 'zh' ? '选择标准需求' : 'Choose standard request')}
                  </button>
                </div>
              );
            })}
          </div>
          )}

          {/* فورم الطلب */}
          <div style={{
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border-muted)',
            padding: '32px 36px',
            borderRadius: 'var(--radius-xl)',
          }}>
            <h3 style={{
              fontSize: 20, fontWeight: 400,
              color: 'var(--text-primary)',
              marginBottom: 6,
              fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              letterSpacing: isAr ? 0 : -0.3,
            }}>
              {isManagedMode
                ? (isAr ? 'طلب مُدار جديد' : lang === 'zh' ? '新的托管需求' : 'New managed sourcing request')
                : (isAr ? 'طلب تسعيرة جديد' : 'New Sourcing Request')}
            </h3>
            <p style={{
              color: 'var(--text-disabled)', fontSize: 13,
              marginBottom: 28, lineHeight: 1.7,
              fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
            }}>
              {isManagedMode
                ? (isAr ? 'معبر سيجهّز الـ brief، يراجع الوضوح، يطابق الموردين المناسبين فقط، ثم يعرض لك أفضل 3 عروض داخل صفحة الطلب نفسها.' : lang === 'zh' ? 'Maabar 会准备 brief、审核清晰度、只匹配合适供应商，然后在同一需求页里展示最佳 3 个方案。' : 'Maabar will prepare the brief, review clarity, match only suitable suppliers, then show the top 3 offers inside the same request page.')
                : (isAr ? 'أكتب تفاصيل المنتج اللي تبيه وسيتواصل معك الموردون بأفضل الأسعار' : 'Describe what you need and suppliers will send you their best prices')}
            </p>

            {/* Category */}
            <div className="form-group">
              <label className={`form-label${isAr ? ' ar' : ''}`}>
                {isAr ? 'نوع المنتج *' : 'Product Category *'}
              </label>
              {/* A SELECT, not 24 pills over four rows (2026-09-21). Same
                  option list, same order and same `cats` source — this is the
                  control changing, not the data.
                  Styled exactly like the delivery-city select below so the two
                  dropdowns on this form match; `.form-input` carries the width,
                  so it cannot overflow its column at narrow widths the way a
                  four-row pill block could.
                  There is deliberately NO empty "choose one" option: category
                  always holds a value (it defaults to 'other'), and an empty
                  first entry would imply the field can be left unset, which the
                  * in the label says it cannot. */}
              <select className="form-input"
                value={newReq.category}
                onChange={e => setNewReq({ ...newReq, category: e.target.value })}
                style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
              >
                {cats.map(c => (
                  <option key={c.val} value={c.val}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className={`form-label${isAr ? ' ar' : ''}`}>
                  {isAr ? 'اسم المنتج بالعربي *' : 'Product Name *'}
                </label>
                <input className="form-input"
                  value={newReq.title_ar}
                  onChange={e => setNewReq({ ...newReq, title_ar: e.target.value })}
                  placeholder={isAr ? 'مثال: كراسي مكتب' : 'e.g. Office Chairs'}
                  dir={isAr ? 'rtl' : 'ltr'}
                />
              </div>
              <div className="form-group">
                {/* The ` ar` modifier was missing here and on no other label in
                    this form (2026-09-21). `.form-label` is 11px, font-sans,
                    letter-spacing 1px; `.form-label.ar` is 13px and font-ar —
                    so in Arabic this one caption rendered in a different face
                    and size from every label around it. The two INPUTS were
                    always the same font: `.form-input` sets font-sans for both
                    name fields, and neither carries an inline override. */}
                <label className={`form-label${isAr ? ' ar' : ''}`}>
                  {isAr ? 'اسم المنتج بالإنجليزي' : 'Product Name (EN)'}
                </label>
                <input className="form-input"
                  value={newReq.title_en}
                  onChange={e => setNewReq({ ...newReq, title_en: e.target.value })}
                  placeholder="e.g. Office Chairs"
                />
              </div>
              {/* Quantity is now a NUMBER plus a UNIT, not one free-text
                  string. The placeholder used to read "500 قطعة" and traders
                  typed exactly that, so the unit lived inside the number and
                  requests.unit (which has existed all along) was null in every
                  row. The RFQ page renders quantity and unit side by side. */}
              <div className="form-group">
                <label className={`form-label${isAr ? ' ar' : ''}`}>
                  {isAr ? 'الكمية المطلوبة *' : lang === 'zh' ? '所需数量 *' : 'Quantity *'}
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-input" inputMode="decimal"
                    value={newReq.quantity}
                    onChange={e => setNewReq({ ...newReq, quantity: normalizeQuantityInput(e.target.value) })}
                    placeholder={isAr ? 'مثال: 500' : lang === 'zh' ? '例：500' : 'e.g. 500'}
                    dir="ltr"
                    style={{ flex: 1 }}
                  />
                  <select className="form-input"
                    value={newReq.unit}
                    onChange={e => setNewReq({ ...newReq, unit: e.target.value })}
                    style={{ width: 120, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                  >
                    {/* No unit preselected (2026-09-28): a 'piece' default saved
                        a unit the trader may never have chosen. The submit
                        already requires one (the city/unit/delivery guard). */}
                    <option value="">{isAr ? 'اختر الوحدة' : lang === 'zh' ? '选择单位' : 'Choose unit'}</option>
                    {REQUEST_UNITS.map(u => (
                      <option key={u.key} value={u.key}>{labelFor(u, lang)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-group">
              {/* REQUIRED as of 2026-09-21 (owner's product decision), in BOTH
                  modes. There is only one description field on this form and
                  managed mode has no second one: the managed brief is DERIVED
                  from this text — managedSourcing.js:99 builds extracted_specs
                  from it and :109 grades ai_confidence by its length (>50
                  high, >20 medium, else low). An empty description therefore
                  produced a low-confidence brief as well as an unquotable RFQ.
                  The zh label is new. This was the only required field on the
                  form whose label was not trilingual — it fell through to the
                  English string — while the hint directly below it already
                  carried all three. */}
              <label className={`form-label${isAr ? ' ar' : ''}`}>
                {isAr ? 'المواصفات المطلوبة *' : lang === 'zh' ? '所需规格 *' : 'Required Specifications *'}
              </label>
              {/* Mirrors the app's request form: a thin description costs the
                  factory a round trip before it can price anything, so the
                  guidance names the four things they always come back asking
                  for. Styled like RequestWizard's field hints. */}
              <div style={{
                fontSize: 12, color: 'var(--text-muted)', marginTop: 2, marginBottom: 6,
                lineHeight: 1.6, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              }}>
                {isAr
                  ? 'اكتب تفاصيل طلبك بوضوح — المواصفات، المقاسات، الكمية، ومستوى الجودة — ليقدّم لك الموردون عرضًا مناسبًا.'
                  : lang === 'zh'
                    ? '清楚描述您的需求 — 规格、尺寸、数量和质量等级 — 以便供应商给出准确报价。'
                    : 'Describe your request clearly — specifications, dimensions, quantity and quality level — so suppliers can quote accurately.'}
              </div>
              <textarea className="form-input" rows={3} required aria-required="true"
                style={{ resize: 'vertical', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                value={newReq.description}
                onChange={e => setNewReq({ ...newReq, description: e.target.value })}
                placeholder={isAr ? 'المواصفات، اللون، الجودة، المعايير...' : 'Specs, color, quality, standards...'}
                dir={isAr ? 'rtl' : 'ltr'}
              />
              <p style={{ fontSize: 11.5, color: 'var(--text-disabled)', margin: '6px 2px 0', lineHeight: 1.7, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {requestContactNote(effectiveLang)}
              </p>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className={`form-label${isAr ? ' ar' : ''}`}>
                  {isAr ? 'الميزانية لكل وحدة' : lang === 'zh' ? '每单位预算' : 'Budget per Unit'}
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-input" type="number" min="0"
                    value={newReq.budget_per_unit}
                    onChange={e => setNewReq({ ...newReq, budget_per_unit: e.target.value })}
                    placeholder={isAr ? 'اختياري' : lang === 'zh' ? '可选' : 'Optional'}
                    dir="ltr"
                    style={{ flex: 1 }}
                  />
                  {/* SAR only (owner, 2026-09-29): a trader never works in USD/CNY. */}
                  <span className="form-input" style={{ width: 90, direction: 'ltr', fontFamily: 'var(--font-sans)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>SAR</span>
                </div>
              </div>
              {/* CHANGED 2026-09-21: no longer managed-only. A direct request
                  is the one a factory races against, and the public RFQ page
                  HIDES the deadline row when it is null — so a direct request
                  reached the factory with no urgency at all, which is the
                  opposite of what a public RFQ link is for. Optional on both
                  paths; the page still fails safe when it is empty. */}
              <div className="form-group">
                <label className={`form-label${isAr ? ' ar' : ''}`}>
                  {isManagedMode
                    ? (isAr ? 'الموعد المستهدف للعروض أو القرار' : lang === 'zh' ? '目标回复 / 决策日期' : 'Target decision / offer deadline')
                    : (isAr ? 'آخر موعد لاستلام العروض (اختياري)' : lang === 'zh' ? '报价截止日期（可选）' : 'Offer deadline (optional)')}
                </label>
                <input className="form-input" type="date"
                  value={newReq.response_deadline || ''}
                  onChange={e => setNewReq({ ...newReq, response_deadline: e.target.value })}
                />
              </div>
            </div>

            {/* ── Destination and delivery ────────────────────────────────
                THE FIELDS A FACTORY CANNOT QUOTE WITHOUT, and neither
                existed until today. requests.ship_city/ship_country have been
                on the table since 20260801000003 with no writer at all, so
                get_public_rfq fell through to the literal 'Saudi Arabia' —
                every factory on every public link was told a constant no
                trader had typed. A factory cannot price CIF or DDP against a
                country name, which is why every quote in production so far is
                EXW, FOB, or carries no basis. */}
            <div className="form-grid">
              <div className="form-group">
                <label className={`form-label${isAr ? ' ar' : ''}`}>
                  {isAr ? 'مدينة التسليم *' : lang === 'zh' ? '收货城市 *' : 'Delivery city *'}
                </label>
                {/* Required; searchable, every city or typed (CityPicker, 2026-10-25).
                    A listed city stores its English name: it travels to a factory. */}
                <CityPicker value={newReq.ship_city} onChange={(v) => setNewReq({ ...newReq, ship_city: v })} lang={lang} />
              </div>
            </div>

            <div className="form-group">
              <label className={`form-label${isAr ? ' ar' : ''}`}>
                {isAr ? 'كيف تبي تستلم البضاعة؟ *' : lang === 'zh' ? '您希望如何收货？ *' : 'How do you want it delivered? *'}
              </label>
              <div style={{
                fontSize: 12, color: 'var(--text-muted)', marginTop: 2, marginBottom: 8,
                lineHeight: 1.6, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              }}>
                {isAr
                  ? 'هذا يحدّد ما الذي يشمله سعر المورّد — وبدونه تقارن عروضاً غير قابلة للمقارنة.'
                  : lang === 'zh'
                    ? '这决定了供应商报价包含哪些费用 — 否则您比较的是无法比较的报价。'
                    : "This decides what the supplier's price covers — without it you are comparing quotes that are not comparable."}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {DELIVERY_PREFERENCES.map(d => {
                  const on = newReq.delivery_preference === d.key;
                  return (
                    <label key={d.key} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                      padding: '10px 12px', borderRadius: 'var(--radius-md)',
                      border: '1px solid', borderColor: on ? 'var(--border-muted)' : 'var(--border-subtle)',
                      background: on ? 'var(--bg-raised)' : 'transparent',
                      fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                    }}>
                      <input type="radio" name="delivery_preference" value={d.key} checked={on}
                        onChange={() => setNewReq({ ...newReq, delivery_preference: d.key })}
                        style={{ accentColor: 'var(--text-secondary)', cursor: 'pointer', marginTop: 3 }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, color: 'var(--text-primary)' }}>
                          {labelFor(d, lang)}
                        </span>
                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-disabled)', marginTop: 2, lineHeight: 1.6 }}>
                          {deliveryHintFor(d, lang)}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="form-group">
              <label className={`form-label${isAr ? ' ar' : ''}`}>
                {isAr ? 'خطة الدفع *' : 'Payment Plan *'}
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* Exactly two plans since 2026-09-28 (owner): 30 (deposit) and
                    100 (full). 50 retired on web and app together. */}
                {[
                  { val: '30', ar: '30% مقدماً', en: '30% upfront' },
                  { val: '100', ar: '100% مقدماً', en: '100% upfront' },
                ].map(p => (
                  <button key={p.val} type="button" onClick={() => setNewReq({ ...newReq, payment_plan: p.val })}
                    style={pillStyle(newReq.payment_plan === p.val, isAr)}>
                    {isAr ? p.ar : p.en}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className={`form-label${isAr ? ' ar' : ''}`}>
                {isAr ? 'متطلبات العينة *' : 'Sample Requirement *'}
              </label>
              {/* A PILL GROUP, matching the payment-plan group directly above
                  (2026-09-21) — same `pillStyle`, same wrapper, so the two
                  single-choice controls on this form cannot drift apart.
                  The three VALUES and the empty default are untouched; what
                  changed is the control and the display order, which now runs
                  strongest-ask-first to match the mobile app. Order is safe to
                  change here only because nothing is preselected — see the
                  note on SAMPLE_REQUIREMENTS.
                  `flexWrap` is what keeps this honest on a narrow phone: three
                  pills wrap onto a second line rather than squeezing. */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {SAMPLE_REQUIREMENTS.map(s => (
                  <button key={s.key} type="button"
                    onClick={() => setNewReq({ ...newReq, sample_requirement: s.key })}
                    style={pillStyle(newReq.sample_requirement === s.key, isAr)}>
                    {isAr ? s.ar : s.en}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact phone — managed mode, signed-in only. A visitor's submit
                is a redirect to sign-in, and this number is not in the draft. */}
            {isManagedMode && user && (
              <div className="form-group">
                <label className={`form-label${isAr ? ' ar' : ''}`}>
                  {isAr ? 'رقم الجوال (واتساب) *' : lang === 'zh' ? '手机号码（WhatsApp）*' : 'Mobile number (WhatsApp) *'}
                </label>
                <PhoneField
                  key={phoneKey}
                  value={phone.e164}
                  onChange={(e164, valid) => { phoneTouched.current = true; setPhone({ e164, valid }); }}
                  defaultCountry="SA"
                  lang={lang}
                  invalid={Boolean(phone.e164) && !phone.valid}
                  inputClassName="form-input"
                  selectClassName="form-input"
                  selectStyle={{ width: 'auto' }}
                  inputStyle={{ minWidth: 0 }}
                />
                <p style={{ fontSize: 11, color: 'var(--text-disabled)', margin: '6px 0 0', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                  {isAr ? 'ليتواصل معك مدير حسابك في معبر بخصوص الطلب.'
                    : lang === 'zh' ? '便于 Maabar 客户经理就此需求与您联系。'
                      : 'So your Maabar account manager can reach you about this request.'}
                </p>
              </div>
            )}

            {isManagedMode && (
              <RequestFeeNotice lang={isAr ? 'ar' : 'en'} config={feeConfig} style={{ marginBottom: 16 }} />
            )}

            {/* Attachments — the PRIVATE request-attachments bucket, and the only
                file input on this form. It replaced a second "Reference Image"
                picker that uploaded to the PUBLIC product-images bucket and wrote
                requests.reference_image; two pickers side by side asked the trader
                to understand a storage decision. Optional throughout: nothing here
                can block the submit. */}
            <div className="form-group">
              <label className={`form-label${isAr ? ' ar' : ''}`}>
                {isAr ? 'مرفقات (اختياري)' : lang === 'zh' ? '附件（可选）' : 'Attachments (optional)'}
              </label>
              <p style={{ fontSize: 11, color: 'var(--text-disabled)', margin: '2px 0 6px', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {isAr ? 'صور، PDF، ZIP — حتى 6 ملفات، 10 ميجابايت لكل ملف.'
                  : lang === 'zh' ? '图片、PDF、ZIP — 最多 6 个文件，每个 10 MB。'
                    : 'Images, PDF, ZIP — up to 6 files, 10 MB each.'}
              </p>
              <label className="btn-outline" style={{ display: 'inline-block', cursor: 'pointer', fontSize: 11, padding: '7px 16px', minHeight: 32 }}>
                {isAr ? 'اختر ملفات' : lang === 'zh' ? '选择文件' : 'Choose files'}
                <input type="file" multiple accept={ATTACH_ACCEPT} style={{ display: 'none' }}
                  onChange={(e) => { picker.addFiles(e.target.files); e.target.value = ''; }} />
              </label>

              {picker.files.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
                  {picker.files.map((f) => (
                    <li key={f.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontSize: 12,
                      flexDirection: isAr ? 'row-reverse' : 'row',
                      fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                    }}>
                      <span dir="ltr" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <button type="button" onClick={() => picker.removeFile(f.id)}
                        style={{ fontSize: 11, color: '#a07070', background: 'none', border: 'none', cursor: 'pointer' }}>
                        {isAr ? 'إزالة' : lang === 'zh' ? '删除' : 'Remove'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {picker.error && <Notice tone="error" isAr={isAr}>{picker.error}</Notice>}
            </div>

            {!user && (
              <p style={{
                fontSize: 12, color: 'var(--text-disabled)',
                marginBottom: 20, padding: '10px 14px',
                background: 'var(--bg-muted)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
              }}>
                {isAr ? 'سيُطلب منك تسجيل الدخول عند إرسال الطلب' : "You'll be asked to sign in when submitting"}
              </p>
            )}

            {/* The outcome of the last submit, where the two alert() calls used
                to be. Rendered here, above the submit button, so it is where the
                trader is already looking rather than over the whole page. */}
            {submitNotice && (
              <Notice tone={submitNotice.tone} isAr={isAr} style={{ whiteSpace: 'pre-line' }}>{submitNotice.text}</Notice>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
              <p style={{ fontSize: 11, color: 'var(--text-disabled)', margin: 0, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                {isManagedMode
                  ? (isAr ? 'في الطلب المُدار سيبقى القرار الأساسي داخل صفحة الطلب نفسها: ستتابع الحالة، ثم ترى العروض المختارة لك، ثم تختار أو تطلب تفاوضاً إضافياً من نفس المكان.' : lang === 'zh' ? '托管需求的主要决策都会留在同一需求页：先看状态，再看为您挑选的方案，然后在同一页做选择或要求继续谈判。' : 'For managed sourcing, the primary decision flow stays inside the same request page: track status, review the selected offers, then choose or request more negotiation there.')
                  : (isAr ? 'يتم حفظ مسودة الطلب تلقائياً على هذا الجهاز حتى لا تفقد التفاصيل قبل الإرسال.' : lang === 'zh' ? '此设备会自动保存草稿，避免在提交前丢失内容。' : 'Your draft auto-saves on this device so you do not lose request details before submission.')}
              </p>
              <button
                className="btn-dark-sm"
                onClick={submitNewRequest}
                disabled={submitting}
                style={{ padding: '12px 28px', fontSize: 14, minHeight: 46 }}>
                {submitting ? '...' : isManagedMode ? (isAr ? 'إرسال الطلب المُدار' : lang === 'zh' ? '提交托管需求' : 'Submit managed request') : (isAr ? 'إرسال الطلب' : lang === 'zh' ? '提交需求' : 'Submit Request')}
              </button>
              {/* A way out for someone who took the wrong path on the home page.
                  Switching keeps what they have already typed rather than
                  sending them back to choose again from an empty form. */}
              <button
                type="button"
                onClick={() => switchMode(isManagedMode ? 'direct' : 'managed')}
                style={{
                  background: 'none', border: 'none', padding: '10px 2px 0', cursor: 'pointer',
                  fontSize: 12.5, color: 'var(--text-secondary)', textDecoration: 'underline',
                  fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)',
                }}>
                {isManagedMode
                  ? (isAr ? 'أو ارفع طلبك بنفسك' : lang === 'zh' ? '或自己发布需求' : 'Or post the request yourself')
                  : (isAr ? 'أو دع معبر يتولّى الطلب' : lang === 'zh' ? '或让 Maabar 代您处理' : 'Or let Maabar handle the request')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
          واجهة المورد
      ════════════════════════════════════ */}
      <Footer lang={lang} />
    </div>
  );
}